import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
    ArrowLeft,
    CheckCircle2,
    MonitorCheck,
    DownloadCloud,
    BookOpen,
    Video,
    Mic2,
    ClipboardList,
    Sparkles,
    ExternalLink,
    Check,
    HelpCircle,
    Layers,
    ShieldCheck,
    Key,
    Copy,
    AlertTriangle
} from 'lucide-react';
import { readinessConfig } from '../../data/readinessData';
import { ResponsiveYouTubeEmbed } from '../../components/student/ResponsiveYouTubeEmbed';

export default function Day1Readiness() {
    const {
        pageTag,
        pageTitle,
        pageSubtitle,
        softwareSection,
        softwareList,
        finalExpectationSection,
        finalCallout
    } = readinessConfig;

    // Software License Keys and backend checklist status
    const [licenseKeys, setLicenseKeys] = useState<{ p6_license_key?: string | null; msp_license_key?: string | null }>({});
    const [copiedKey, setCopiedKey] = useState<string | null>(null);
    const [isCompletedServer, setIsCompletedServer] = useState(false);
    const [savingChecklist, setSavingChecklist] = useState(false);

    // Interactive student checklist state with initial fallback from localStorage
    const [checkedItems, setCheckedItems] = useState<{ [key: string]: boolean }>(() => {
        try {
            const saved = localStorage.getItem('lgl_day1_readiness_checks');
            return saved ? JSON.parse(saved) : {};
        } catch {
            return {};
        }
    });

    useEffect(() => {
        const fetchReadinessData = async () => {
            const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
            const token = localStorage.getItem('token');
            if (!token) return;
            try {
                const res = await fetch(`${API_URL}/student/readiness-status`, {
                    headers: {
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data.checklist) {
                        setCheckedItems(data.checklist);
                        try {
                            localStorage.setItem('lgl_day1_readiness_checks', JSON.stringify(data.checklist));
                        } catch {}
                    }
                    setIsCompletedServer(!!data.is_completed);
                    setLicenseKeys({
                        p6_license_key: data.p6_license_key,
                        msp_license_key: data.msp_license_key
                    });
                }
            } catch (err) {
                // Silently fallback if offline
            }
        };
        fetchReadinessData();
    }, []);

    const handleCopyKey = (key: string, label: string) => {
        navigator.clipboard.writeText(key);
        setCopiedKey(key);
        toast.success(`${label} license key copied to clipboard!`);
        setTimeout(() => setCopiedKey(null), 2500);
    };

    const toggleCheck = async (id: string) => {
        const nextState = !checkedItems[id];
        const updated = { ...checkedItems, [id]: nextState };
        if (!nextState) {
            delete updated[id];
        }
        setCheckedItems(updated);

        try {
            localStorage.setItem('lgl_day1_readiness_checks', JSON.stringify(updated));
        } catch {}

        // Persist to backend database
        const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
        const token = localStorage.getItem('token');
        if (!token) return;

        setSavingChecklist(true);
        try {
            const res = await fetch(`${API_URL}/student/readiness-status`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    item_id: id,
                    completed: nextState,
                    checklist: updated
                })
            });
            if (res.ok) {
                const data = await res.json();
                setIsCompletedServer(!!data.is_completed);
            }
        } catch (err) {
            // Silently maintain local state
        } finally {
            setSavingChecklist(false);
        }
    };

    // Calculate overall completion count
    const totalRequired = softwareList.length + finalExpectationSection.checklist.length;
    const completedCount = Object.values(checkedItems).filter(Boolean).length;
    const completionPercentage = Math.round((completedCount / totalRequired) * 100);

    const getSidebarIcon = (iconName: string) => {
        switch (iconName) {
            case 'BookOpen':
                return <BookOpen size={20} />;
            case 'Video':
                return <Video size={20} />;
            case 'Mic2':
                return <Mic2 size={20} />;
            case 'ClipboardList':
                return <ClipboardList size={20} />;
            default:
                return <Layers size={20} />;
        }
    };

    return (
        <div className="space-y-8 sm:space-y-10 pb-20 max-w-[1280px] mx-auto px-4 sm:px-6 md:px-8 text-brand-charcoal dark:text-white">
            {/* Top Navigation & Breadcrumb */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
                <Link
                    to="/student/dashboard"
                    className="inline-flex items-center gap-2 text-xs font-black text-brand-muted hover:text-brand-emerald uppercase tracking-widest transition-colors group no-underline"
                >
                    <ArrowLeft size={16} className="transform group-hover:-translate-x-1 transition-transform" />
                    Back to Dashboard
                </Link>

                <div className="flex items-center gap-2 px-3 py-1.5 bg-brand-emerald/10 text-brand-emerald rounded-full border border-brand-emerald/20 text-[11px] font-bold self-start sm:self-auto">
                    <Sparkles size={14} />
                    <span>Permanent Student Preparation Portal</span>
                </div>
            </div>

            {/* Main Header / Banner */}
            <header className="relative bg-white dark:bg-brand-charcoal rounded-3xl p-6 sm:p-10 border border-brand-border shadow-sm overflow-hidden space-y-6">
                <div className="absolute top-0 right-0 w-96 h-96 bg-brand-emerald/10 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20" />

                <div className="relative z-10 max-w-3xl space-y-3">
                    <div className="flex items-center gap-2">
                        <span className="text-brand-emerald font-black text-xs uppercase tracking-[0.2em]">
                            {pageTag}
                        </span>
                    </div>

                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-brand-charcoal dark:text-white">
                        {pageTitle}
                    </h1>

                    <p className="text-sm sm:text-base md:text-lg text-brand-muted dark:text-gray-300 font-medium leading-relaxed">
                        {pageSubtitle}
                    </p>
                </div>

                {/* Interactive Readiness Progress Summary */}
                <div className="relative z-10 p-5 bg-brand-beige/50 dark:bg-white/5 rounded-2xl border border-brand-border flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-black uppercase tracking-widest text-brand-charcoal dark:text-white">
                                Preparation Checklist Tracker
                            </span>
                            <span className="px-2 py-0.5 bg-brand-emerald text-white rounded-md text-[10px] font-bold">
                                {completedCount} of {totalRequired} Ready
                            </span>
                            {savingChecklist && (
                                <span className="text-[10px] font-bold text-brand-muted animate-pulse">
                                    Saving...
                                </span>
                            )}
                            {completionPercentage === 100 && (
                                <span className="px-2 py-0.5 bg-emerald-500 text-white rounded-md text-[10px] font-black uppercase tracking-wider flex items-center gap-1">
                                    <Check size={12} strokeWidth={3} /> Day 1 Ready!
                                </span>
                            )}
                        </div>
                        <p className="text-xs text-brand-muted font-medium">
                            Your preparation progress is saved directly to your account and preserved across all sessions.
                        </p>
                    </div>

                    <div className="w-full sm:w-56 space-y-1.5 shrink-0">
                        <div className="flex justify-between text-[11px] font-black text-brand-muted">
                            <span>Readiness</span>
                            <span className="text-brand-emerald">{completionPercentage}%</span>
                        </div>
                        <div className="w-full h-2.5 bg-brand-border dark:bg-white/10 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-brand-emerald transition-all duration-500 ease-out"
                                style={{ width: `${completionPercentage}%` }}
                            />
                        </div>
                    </div>
                </div>
            </header>

            {/* ========================================================================= */}
            {/* 01 — SOFTWARE INSTALLATION (Overview Checklist Cards) */}
            {/* ========================================================================= */}
            <section className="space-y-6">
                <div className="space-y-2 border-b border-brand-border pb-4">
                    <div className="flex items-center gap-3">
                        <span className="w-9 h-9 rounded-xl bg-brand-emerald text-white font-black text-sm flex items-center justify-center shrink-0 shadow-md shadow-brand-emerald/20">
                            {softwareSection.number}
                        </span>
                        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-brand-charcoal dark:text-white uppercase">
                            {softwareSection.title}
                        </h2>
                    </div>
                    <p className="text-sm sm:text-base text-brand-muted font-medium pl-12">
                        {softwareSection.instruction}
                    </p>
                </div>

                {/* Mac (macOS) Parallels Desktop Requirement Notice */}
                <div className="p-5 bg-gradient-to-r from-amber-500/15 via-amber-500/10 to-transparent dark:from-amber-500/20 dark:via-amber-500/10 rounded-2xl md:rounded-3xl border border-amber-500/30 flex flex-col sm:flex-row items-start sm:items-center gap-4 shadow-sm">
                    <div className="w-12 h-12 rounded-2xl bg-amber-500 text-white flex items-center justify-center shrink-0 shadow-md shadow-amber-500/25">
                        <AlertTriangle size={24} />
                    </div>
                    <div className="space-y-1 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="text-sm sm:text-base font-black text-amber-950 dark:text-amber-100 uppercase tracking-tight">
                                Important Requirement for Mac (macOS) Users
                            </h4>
                            <span className="px-2.5 py-0.5 bg-amber-500/20 text-amber-800 dark:text-amber-200 border border-amber-500/30 rounded-full text-[10px] font-black uppercase tracking-wider">
                                Parallels Desktop Required
                            </span>
                        </div>
                        <p className="text-xs sm:text-sm text-amber-900/90 dark:text-amber-200/90 font-medium leading-relaxed">
                            <strong>Primavera P6</strong> and <strong>Microsoft Project (MSP)</strong> are native Windows desktop applications. If you are using a Mac (Apple Silicon M-Series or Intel), you <strong>must install Parallels Desktop (running Windows)</strong> on your Mac before the installation of Primavera P6 and Microsoft Project can commence.
                        </p>
                    </div>
                </div>

                {/* 3 Software Overview Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                    {softwareList.map((software) => {
                        const isDone = !!checkedItems[software.id];
                        const requiresParallels = software.id === 'primavera-p6' || software.id === 'microsoft-project';
                        return (
                            <div
                                key={software.id}
                                className={`bg-white dark:bg-brand-charcoal rounded-2xl sm:rounded-3xl p-6 border transition-all duration-300 flex flex-col justify-between space-y-5 ${
                                    isDone
                                        ? 'border-emerald-500/40 shadow-lg shadow-emerald-500/5'
                                        : 'border-brand-border hover:shadow-lg hover:-translate-y-0.5'
                                }`}
                            >
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <span className="px-3 py-1 bg-brand-emerald/10 text-brand-emerald border border-brand-emerald/20 rounded-full text-[10px] font-black uppercase tracking-widest">
                                                Step {software.stepNumber}
                                            </span>
                                            {requiresParallels && (
                                                <span className="px-2 py-0.5 bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/20 rounded-full text-[9px] font-bold uppercase tracking-wider">
                                                    Mac: Needs Parallels
                                                </span>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => toggleCheck(software.id)}
                                            className={`p-1.5 rounded-xl text-xs font-bold transition-colors flex items-center gap-1.5 border ${
                                                isDone
                                                    ? 'bg-emerald-500 text-white border-emerald-500'
                                                    : 'bg-brand-beige dark:bg-white/5 text-brand-muted border-brand-border hover:text-brand-charcoal dark:hover:text-white'
                                            }`}
                                            title="Mark installation complete"
                                        >
                                            <Check size={14} strokeWidth={3} />
                                            <span>{isDone ? 'Installed' : 'Mark Done'}</span>
                                        </button>
                                    </div>

                                    <h3 className="text-lg sm:text-xl font-black text-brand-charcoal dark:text-white">
                                        {software.name}
                                    </h3>

                                    <p className="text-xs text-brand-muted font-medium leading-relaxed">
                                        {software.summary}
                                    </p>
                                </div>

                                <div className="flex flex-col gap-2 pt-3 border-t border-brand-border/60">
                                    {software.downloadUrl && (
                                        <a
                                            href={software.downloadUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-full inline-flex items-center justify-center gap-2 py-3 bg-brand-emerald hover:bg-brand-emerald/90 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-md shadow-brand-emerald/20 no-underline hover:scale-[1.02] active:scale-[0.98]"
                                        >
                                            <DownloadCloud size={15} />
                                            <span>Download {software.shortName}</span>
                                            <ExternalLink size={12} className="opacity-80" />
                                        </a>
                                    )}
                                    <a
                                        href={`#step-${software.stepNumber}`}
                                        className="w-full inline-flex items-center justify-center gap-2 py-2.5 bg-brand-beige dark:bg-white/5 hover:bg-brand-emerald hover:text-white dark:hover:bg-brand-emerald text-brand-charcoal dark:text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all no-underline border border-brand-border"
                                    >
                                        <span>Jump to Video Guide ↓</span>
                                    </a>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* ========================================================================= */}
            {/* 02 — PRIMAVERA P6 INSTALLATION (Step + Responsive Video) */}
            {/* ========================================================================= */}
            {softwareList[0] && (
                <section id="step-02" className="space-y-6 pt-4 scroll-mt-24">
                    <div className="bg-white dark:bg-brand-charcoal rounded-3xl p-6 sm:p-8 border border-brand-border shadow-sm space-y-6">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-brand-border/60 pb-6">
                            <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                    <span className="px-3 py-0.5 bg-brand-emerald text-white rounded-full text-[10px] font-black uppercase tracking-widest">
                                        Step 02
                                    </span>
                                    <span className="text-xs font-black text-brand-muted uppercase tracking-widest">
                                        Software Setup
                                    </span>
                                </div>
                                <h2 className="text-2xl sm:text-3xl font-black text-brand-charcoal dark:text-white tracking-tight">
                                    {softwareList[0].sectionTitle}
                                </h2>
                                <p className="text-xs sm:text-sm text-brand-muted font-medium">
                                    {softwareList[0].description}
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={() => toggleCheck(softwareList[0].id)}
                                className={`px-5 py-3 rounded-2xl text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2 self-start sm:self-auto border ${
                                    checkedItems[softwareList[0].id]
                                        ? 'bg-emerald-500 text-white border-emerald-500 shadow-lg shadow-emerald-500/20'
                                        : 'bg-brand-beige dark:bg-white/5 text-brand-charcoal dark:text-white border-brand-border hover:border-brand-emerald'
                                }`}
                            >
                                <Check size={16} strokeWidth={3} />
                                <span>{checkedItems[softwareList[0].id] ? 'Installed & Ready' : 'Mark Step 02 Complete'}</span>
                            </button>
                        </div>

                        {/* Download Installer Banner */}
                        {softwareList[0].downloadUrl && (
                            <div className="p-5 sm:p-6 bg-gradient-to-r from-brand-emerald/10 via-brand-emerald/5 to-transparent rounded-2xl md:rounded-3xl border border-brand-emerald/30 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-brand-emerald text-white flex items-center justify-center shrink-0 shadow-md shadow-brand-emerald/25">
                                        <DownloadCloud size={24} />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h4 className="text-base font-black text-brand-charcoal dark:text-white">
                                                Download Primavera P6 Installer
                                            </h4>
                                            <span className="px-2.5 py-0.5 bg-brand-emerald/20 text-brand-emerald rounded-full text-[10px] font-black uppercase tracking-wider">
                                                Official Archive
                                            </span>
                                        </div>
                                        <p className="text-xs text-brand-muted font-medium mt-0.5">
                                            Download the Primavera P6 software package directly to your computer before following the video guide.
                                        </p>
                                    </div>
                                </div>

                                <a
                                    href={softwareList[0].downloadUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center gap-2.5 px-6 sm:px-7 py-3.5 bg-brand-emerald hover:bg-brand-emerald/90 text-white rounded-2xl text-xs font-black uppercase tracking-wider transition-all shadow-lg shadow-brand-emerald/25 hover:scale-105 active:scale-95 shrink-0 no-underline"
                                >
                                    <DownloadCloud size={16} />
                                    <span>Download P6 Software</span>
                                    <ExternalLink size={14} />
                                </a>
                            </div>
                        )}

                        {/* Responsive YouTube Video Embed */}
                        <ResponsiveYouTubeEmbed
                            videoId={softwareList[0].videoId}
                            videoUrl={softwareList[0].videoUrl}
                            title={softwareList[0].videoTitle}
                            description="Watch this comprehensive walkthrough for step-by-step Primavera P6 installation and configuration."
                            stepBadge="Step 02: Primavera P6"
                        />

                        {/* Helpful Prerequisites & Mac Compatibility Notes */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="p-4 bg-amber-500/10 dark:bg-amber-500/5 rounded-2xl border border-amber-500/30 flex items-start gap-3 text-xs text-amber-900 dark:text-amber-200">
                                <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                                <p className="leading-relaxed">
                                    <strong className="text-amber-950 dark:text-white font-bold">Mac Installation Requirement:</strong> Primavera P6 is a Windows-native application. Installation for Mac requires <strong>Parallels Desktop</strong> (with Windows) to be installed before the installation can commence.
                                </p>
                            </div>
                            <div className="p-4 bg-brand-beige/50 dark:bg-white/5 rounded-2xl border border-brand-border flex items-start gap-3 text-xs text-brand-muted">
                                <ShieldCheck size={18} className="text-brand-emerald shrink-0 mt-0.5" />
                                <p className="leading-relaxed">
                                    <strong className="text-brand-charcoal dark:text-white font-bold">Admin Privileges:</strong> Make sure you have administrator privileges on your computer before running the installer so database configurations apply without permission conflicts.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* ========================================================================= */}
            {/* 03 — MICROSOFT PROJECT (MSP) INSTALLATION (Step + Responsive Video) */}
            {/* ========================================================================= */}
            {softwareList[1] && (
                <section id="step-03" className="space-y-6 pt-4 scroll-mt-24">
                    <div className="bg-white dark:bg-brand-charcoal rounded-3xl p-6 sm:p-8 border border-brand-border shadow-sm space-y-6">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-brand-border/60 pb-6">
                            <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                    <span className="px-3 py-0.5 bg-brand-emerald text-white rounded-full text-[10px] font-black uppercase tracking-widest">
                                        Step 03
                                    </span>
                                    <span className="text-xs font-black text-brand-muted uppercase tracking-widest">
                                        Software Setup
                                    </span>
                                </div>
                                <h2 className="text-2xl sm:text-3xl font-black text-brand-charcoal dark:text-white tracking-tight">
                                    {softwareList[1].sectionTitle}
                                </h2>
                                <p className="text-xs sm:text-sm text-brand-muted font-medium">
                                    {softwareList[1].description}
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={() => toggleCheck(softwareList[1].id)}
                                className={`px-5 py-3 rounded-2xl text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2 self-start sm:self-auto border ${
                                    checkedItems[softwareList[1].id]
                                        ? 'bg-emerald-500 text-white border-emerald-500 shadow-lg shadow-emerald-500/20'
                                        : 'bg-brand-beige dark:bg-white/5 text-brand-charcoal dark:text-white border-brand-border hover:border-brand-emerald'
                                }`}
                            >
                                <Check size={16} strokeWidth={3} />
                                <span>{checkedItems[softwareList[1].id] ? 'Installed & Ready' : 'Mark Step 03 Complete'}</span>
                            </button>
                        </div>

                        {/* Side-by-Side Grid: Download Software & License Key */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                            {/* Card 1: Download MSP Installer */}
                            {softwareList[1].downloadUrl && (
                                <div className="p-6 bg-gradient-to-br from-brand-emerald/10 via-brand-emerald/5 to-transparent rounded-2xl md:rounded-3xl border border-brand-emerald/30 flex flex-col justify-between space-y-4 shadow-sm">
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="w-12 h-12 rounded-2xl bg-brand-emerald text-white flex items-center justify-center shrink-0 shadow-md shadow-brand-emerald/25">
                                                <DownloadCloud size={24} />
                                            </div>
                                            <span className="px-3 py-1 bg-brand-emerald/20 text-brand-emerald rounded-full text-[10px] font-black uppercase tracking-wider">
                                                Direct Download
                                            </span>
                                        </div>
                                        <div>
                                            <h4 className="text-base font-black text-brand-charcoal dark:text-white">
                                                Download Microsoft Project (MSP) Installer
                                            </h4>
                                            <p className="text-xs text-brand-muted font-medium mt-1 leading-relaxed">
                                                Download the Microsoft Project installation files to your computer before running the setup.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="pt-2">
                                        <a
                                            href={softwareList[1].downloadUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-full inline-flex items-center justify-center gap-2.5 py-3.5 bg-brand-emerald hover:bg-brand-emerald/90 text-white rounded-2xl text-xs font-black uppercase tracking-wider transition-all shadow-lg shadow-brand-emerald/25 hover:scale-[1.02] active:scale-[0.98] no-underline"
                                        >
                                            <DownloadCloud size={16} />
                                            <span>Download MSP Software</span>
                                            <ExternalLink size={14} />
                                        </a>
                                    </div>
                                </div>
                            )}

                            {/* Card 2: Your Microsoft Project (MSP) License Key */}
                            <div className="p-6 bg-brand-beige/50 dark:bg-white/5 rounded-2xl md:rounded-3xl border border-brand-border flex flex-col justify-between space-y-4 shadow-sm">
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="w-12 h-12 rounded-2xl bg-brand-emerald/10 text-brand-emerald flex items-center justify-center shrink-0 border border-brand-emerald/20">
                                            <Key size={22} />
                                        </div>
                                        {licenseKeys.msp_license_key ? (
                                            <span className="px-3 py-1 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5">
                                                <CheckCircle2 size={12} /> Assigned
                                            </span>
                                        ) : (
                                            <span className="px-3 py-1 bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 rounded-full text-[10px] font-black uppercase tracking-widest">
                                                Key Pending Assignment
                                            </span>
                                        )}
                                    </div>
                                    <div>
                                        <h4 className="text-base font-black text-brand-charcoal dark:text-white">
                                            Your Microsoft Project (MSP) License Key
                                        </h4>
                                        <p className="text-xs text-brand-muted font-medium mt-1 leading-relaxed">
                                            Use this license key to activate Microsoft Project on your workstation.
                                        </p>
                                    </div>
                                </div>

                                <div className="pt-2">
                                    {licenseKeys.msp_license_key ? (
                                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                                            <div className="flex-1 p-3 bg-white dark:bg-brand-charcoal rounded-xl border border-brand-border font-mono text-xs font-bold text-brand-charcoal dark:text-white select-all overflow-x-auto shadow-inner">
                                                {licenseKeys.msp_license_key}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleCopyKey(licenseKeys.msp_license_key!, 'Microsoft Project')}
                                                className="px-5 py-3 bg-brand-emerald hover:bg-brand-emerald/90 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-md shadow-brand-emerald/20 flex items-center justify-center gap-1.5 active:scale-95 shrink-0"
                                            >
                                                {copiedKey === licenseKeys.msp_license_key ? (
                                                    <><Check size={14} strokeWidth={3} /> Copied!</>
                                                ) : (
                                                    <><Copy size={14} /> Copy Key</>
                                                )}
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="p-3 bg-white/60 dark:bg-white/5 rounded-xl border border-dashed border-brand-border text-xs text-brand-muted font-medium">
                                            💡 Your instructor will assign your personalized Microsoft Project license key before class begins. Check back here or in your cohort announcements.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Responsive YouTube Video Embed */}
                        <ResponsiveYouTubeEmbed
                            videoId={softwareList[1].videoId}
                            videoUrl={softwareList[1].videoUrl}
                            title={softwareList[1].videoTitle}
                            description="Follow along with the video guide to install and license Microsoft Project (MSP) for your cohort assignments."
                            stepBadge="Step 03: Microsoft Project"
                        />

                        {/* Helpful Prerequisites & Mac Compatibility Notes */}
                        <div className="p-4 sm:p-5 bg-amber-500/10 dark:bg-amber-500/5 rounded-2xl border border-amber-500/30 flex items-start gap-3 text-xs sm:text-sm text-amber-900 dark:text-amber-200">
                            <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                            <p className="leading-relaxed">
                                <strong className="text-amber-950 dark:text-white font-bold">Mac Installation Requirement:</strong> Microsoft Project (MSP) is a native Windows desktop application. Installation for Mac requires <strong>Parallels Desktop</strong> (with Windows) to be installed on your Mac before the Microsoft Project installation can commence.
                            </p>
                        </div>
                    </div>
                </section>
            )}

            {/* ========================================================================= */}
            {/* 04 — MICROSOFT TEAMS INSTALLATION (Exact Provided Video) */}
            {/* ========================================================================= */}
            {softwareList[2] && (
                <section id="step-04" className="space-y-6 pt-4 scroll-mt-24">
                    <div className="bg-white dark:bg-brand-charcoal rounded-3xl p-6 sm:p-8 border border-brand-border shadow-sm space-y-6">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-brand-border/60 pb-6">
                            <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                    <span className="px-3 py-0.5 bg-brand-emerald text-white rounded-full text-[10px] font-black uppercase tracking-widest">
                                        Step 04
                                    </span>
                                    <span className="text-xs font-black text-brand-muted uppercase tracking-widest">
                                        Collaboration Hub
                                    </span>
                                </div>
                                <h2 className="text-2xl sm:text-3xl font-black text-brand-charcoal dark:text-white tracking-tight">
                                    {softwareList[2].sectionTitle}
                                </h2>
                                <p className="text-xs sm:text-sm text-brand-muted font-medium">
                                    {softwareList[2].description}
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={() => toggleCheck(softwareList[2].id)}
                                className={`px-5 py-3 rounded-2xl text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2 self-start sm:self-auto border ${
                                    checkedItems[softwareList[2].id]
                                        ? 'bg-emerald-500 text-white border-emerald-500 shadow-lg shadow-emerald-500/20'
                                        : 'bg-brand-beige dark:bg-white/5 text-brand-charcoal dark:text-white border-brand-border hover:border-brand-emerald'
                                }`}
                            >
                                <Check size={16} strokeWidth={3} />
                                <span>{checkedItems[softwareList[2].id] ? 'Installed & Ready' : 'Mark Step 04 Complete'}</span>
                            </button>
                        </div>

                        {/* Responsive YouTube Video Embed with Provided Video */}
                        <ResponsiveYouTubeEmbed
                            videoId={softwareList[2].videoId}
                            videoUrl={softwareList[2].videoUrl}
                            title={softwareList[2].videoTitle}
                            description="Official Microsoft Teams installation and onboarding tutorial. Please ensure Teams is set up prior to the live sessions."
                            stepBadge="Step 04: Microsoft Teams"
                        />

                        {/* Helpful Prerequisites Note */}
                        <div className="p-4 bg-brand-beige/50 dark:bg-white/5 rounded-2xl border border-brand-border flex items-start gap-3 text-xs text-brand-muted">
                            <ShieldCheck size={18} className="text-brand-emerald shrink-0 mt-0.5" />
                            <p className="leading-relaxed">
                                <strong className="text-brand-charcoal dark:text-white font-bold">Audio & Video Check:</strong> After installing Teams, open your Teams Settings &gt; Devices and test your microphone and camera to ensure clear communication on Day One.
                            </p>
                        </div>
                    </div>
                </section>
            )}

            {/* ========================================================================= */}
            {/* FINAL DAY 1 READINESS EXPECTATION (Sidebar Access Checklist) */}
            {/* ========================================================================= */}
            <section className="bg-gradient-to-br from-brand-charcoal via-slate-900 to-brand-charcoal text-white rounded-3xl p-6 sm:p-10 border border-brand-border/40 shadow-2xl space-y-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-80 h-80 bg-brand-emerald/15 rounded-full blur-3xl pointer-events-none" />

                <div className="relative z-10 space-y-3 max-w-3xl">
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full border border-white/20 text-[10px] font-black uppercase tracking-widest text-brand-emerald">
                        <MonitorCheck size={14} />
                        <span>Platform Verification</span>
                    </div>

                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-black uppercase tracking-tight text-white">
                        {finalExpectationSection.title}
                    </h2>

                    <p className="text-xs sm:text-sm md:text-base text-gray-300 font-medium leading-relaxed">
                        {finalExpectationSection.instruction}
                    </p>
                </div>

                {/* 4 Sidebar Verification Check items */}
                <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    {finalExpectationSection.checklist.map((item) => {
                        const isChecked = !!checkedItems[item.id];
                        return (
                            <div
                                key={item.id}
                                className={`p-5 sm:p-6 rounded-2xl border transition-all duration-300 flex flex-col justify-between gap-4 ${
                                    isChecked
                                        ? 'bg-white/10 border-emerald-500/60 shadow-lg'
                                        : 'bg-white/5 border-white/10 hover:bg-white/[0.08] hover:border-white/20'
                                }`}
                            >
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-brand-emerald/20 text-brand-emerald flex items-center justify-center shrink-0 border border-brand-emerald/30">
                                                {getSidebarIcon(item.iconName)}
                                            </div>
                                            <h3 className="text-lg font-black text-white flex items-center gap-2">
                                                <span>{item.name}</span>
                                                {isChecked && <CheckCircle2 size={16} className="text-emerald-400" />}
                                            </h3>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() => toggleCheck(item.id)}
                                            className={`p-2 rounded-xl text-xs font-bold transition-all border ${
                                                isChecked
                                                    ? 'bg-emerald-500 text-white border-emerald-500'
                                                    : 'bg-white/10 text-gray-300 border-white/20 hover:bg-white/20'
                                            }`}
                                            title="Check off this requirement"
                                        >
                                            <Check size={14} strokeWidth={3} />
                                        </button>
                                    </div>

                                    <p className="text-xs text-gray-300 font-medium leading-relaxed">
                                        {item.description}
                                    </p>
                                </div>

                                <div className="pt-3 border-t border-white/10 flex items-center justify-between">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                                        Sidebar: {item.name}
                                    </span>
                                    <Link
                                        to={item.path}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 text-xs font-black text-brand-emerald hover:text-white uppercase tracking-wider transition-colors"
                                    >
                                        <span>Test Access</span>
                                        <ExternalLink size={12} />
                                    </Link>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* ========================================================================= */}
            {/* FINAL READINESS MESSAGE (Bottom Callout) */}
            {/* ========================================================================= */}
            <section className="bg-white dark:bg-brand-charcoal rounded-3xl p-8 sm:p-12 border-2 border-brand-emerald/40 shadow-xl text-center space-y-6 relative overflow-hidden">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-3xl bg-brand-emerald/10 text-brand-emerald flex items-center justify-center mx-auto border border-brand-emerald/30 shadow-inner">
                    <Sparkles size={36} className="sm:size-10" />
                </div>

                <div className="space-y-3 max-w-2xl mx-auto">
                    <span className="px-4 py-1 bg-brand-emerald text-white rounded-full text-[10px] font-black uppercase tracking-[0.2em]">
                        {finalCallout.title}
                    </span>

                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-brand-charcoal dark:text-white tracking-tight uppercase">
                        {finalCallout.subtitle}
                    </h2>

                    <p className="text-xs sm:text-sm text-brand-muted font-medium leading-relaxed">
                        {finalCallout.encouragement}
                    </p>
                </div>

                <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Link
                        to="/student/dashboard"
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-charcoal dark:bg-white text-white dark:text-brand-charcoal rounded-2xl font-black text-xs uppercase tracking-widest hover:opacity-90 active:scale-95 transition-all no-underline shadow-lg"
                    >
                        <ArrowLeft size={16} /> Return to Dashboard
                    </Link>

                    <Link
                        to="/student/courses"
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-emerald text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-brand-emerald/90 active:scale-95 transition-all no-underline shadow-lg shadow-brand-emerald/20"
                    >
                        <BookOpen size={16} /> Explore Courses
                    </Link>
                </div>
            </section>
        </div>
    );
}
